import React from "react";
import {
  Autocomplete,
  CircularProgress,
  TextField,
  Box,
  Typography,
} from "@mui/material";

const CustomAutoComplete = ({
  options = [],
  isLoading = false,
  onChange,
  placeholder = "Search and select...",
  onInputChange,
  inputValue = "",
  value = null,
  error = false,
  helperText = "",
  required = false,
  label = "",
  disabled = false,
  size = "small",
  fullWidth = true,
  noOptionsText = "No options found",
  loadingText = "Loading...",
  clearable = true,
  multiple = false,
  filterSelectedOptions = false,
  limitTags = -1,
  variant = "outlined",
}) => {
  const selectedOption = multiple
    ? options.filter((opt) =>
        Array.isArray(value) ? value.includes(opt.id) : false
      )
    : options.find((opt) => opt.id === value) || null;

  const handleChange = (e, newValue) => {
    if (multiple) {
      const selectedIds = Array.isArray(newValue)
        ? newValue.map((option) => option.id)
        : [];
      onChange(selectedIds);
    } else {
      onChange(newValue?.id || null);
    }
  };

  const handleInputChange = (e, newInputValue, reason) => {
    if (onInputChange) {
      onInputChange(newInputValue, reason);
    }
  };

  return (
    <Autocomplete
      options={options}
      getOptionLabel={(option) => option?.name || ""}
      isOptionEqualToValue={(option, val) => option?.id === val?.id}
      value={selectedOption}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      loading={isLoading}
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
      multiple={multiple}
      filterSelectedOptions={filterSelectedOptions}
      limitTags={limitTags}
      disableClearable={!clearable}
      noOptionsText={isLoading ? loadingText : noOptionsText}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: "8px",
          backgroundColor: disabled ? "#f1f5f9" : "#ffffff",
          transition: "all 0.2s ease",
          padding: "0px",
          '& fieldset': {
            borderColor: error ? "#dc2626" : "#e2e8f0",
            borderWidth: error ? "2px" : "1.5px",
          },
          '&:hover fieldset': {
            borderColor: error ? "#dc2626" : "#94a3b8",
            borderWidth: error ? "2px" : "1.5px",
          },
          '&.Mui-focused fieldset': {
            borderColor: error ? "#dc2626" : "#64748b",
            borderWidth: "2px",
            boxShadow: error 
              ? "0 0 0 3px rgba(220, 38, 38, 0.1)" 
              : "0 0 0 3px rgba(100, 116, 139, 0.1)",
          },
        },
        '& .MuiInputLabel-root': {
          fontSize: "0.85rem",
          color: "#64748b",
          fontWeight: 500,
          '&.Mui-focused': {
            color: "#475569",
          },
          '&.Mui-error': {
            color: "#dc2626",
          },
        },
        '& .MuiInputBase-input': {
          fontSize: "0.875rem",
          fontWeight: 500,
          color: "#1e293b",
          padding: "10px 14px",
          '&::placeholder': {
            color: "#94a3b8",
            fontWeight: 400,
            opacity: 1,
          },
        },
        '& .MuiAutocomplete-clearIndicator': {
          color: "#94a3b8",
          '&:hover': {
            color: "#475569",
          },
        },
        '& .MuiAutocomplete-popupIndicator': {
          color: "#94a3b8",
          '&:hover': {
            color: "#475569",
          },
        },
        '& .MuiFormHelperText-root': {
          fontSize: "0.75rem",
          fontWeight: 500,
          marginLeft: 0,
          marginTop: "4px",
          color: error ? "#dc2626" : "#94a3b8",
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          required={required}
          variant={variant}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box 
          component="li" 
          {...props} 
          key={option.id}
          sx={{
            fontSize: "0.875rem",
            padding: "10px 16px",
            fontWeight: 400,
            color: "#1e293b",
            transition: "all 0.15s ease",
            borderBottom: "1px solid #f1f5f9",
            '&:last-child': {
              borderBottom: "none",
            },
            '&:hover': {
              backgroundColor: "#f1f5f9",
            },
            '&.Mui-selected': {
              backgroundColor: "#e2e8f0",
              color: "#0f172a",
              fontWeight: 600,
              '&:hover': {
                backgroundColor: "#d1d5db",
              },
            },
            '&.Mui-focusVisible': {
              backgroundColor: "#f1f5f9",
            },
          }}
        >
          <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>{option.name}</Typography>
        </Box>
      )}
      PaperProps={{
        sx: {
          borderRadius: "8px",
          marginTop: "4px",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
          border: "1px solid #e2e8f0",
          overflow: "hidden",
          maxHeight: "300px",
        },
      }}
    />
  );
};

export default CustomAutoComplete;